import { useEffect, useState, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { FilePdf, UploadSimple, Trash, Code } from "@phosphor-icons/react";

export default function Vorlagen() {
  const [templates, setTemplates] = useState([]);
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    const { data } = await api.get("/templates");
    setTemplates(data);
  };

  useEffect(() => {
    load();
  }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Bitte eine PDF-Datei wählen");
    if (!name.trim()) return toast.error("Bitte einen Namen angeben");
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", name);
    try {
      await api.post("/templates", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Vorlage hochgeladen");
      setName("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/templates/${id}`);
    setTemplates((t) => t.filter((x) => x.id !== id));
    toast.success("Vorlage gelöscht");
  };

  return (
    <div className="p-8 max-w-5xl">
      <div className="mb-8">
        <h1 className="font-heading text-4xl font-bold tracking-tight">Vorlagen</h1>
        <p className="text-sm text-zinc-500 mt-1">
          PDF-Vorlagen mit Platzhaltern im Format <code className="text-[#002FA7]">{"{{variable}}"}</code> hochladen.
          Verfügbare Felder: name, name_for_emails, email, telefon, website, bewertung, street, strasse, hausnummer, postal_code, city, firmentyp, gruendungsdatum, beschreibung, tags, geschaeftsfuehrung, datum.
        </p>
      </div>

      <form onSubmit={upload} data-testid="template-upload-form" className="bg-white border border-zinc-200 rounded-md p-6 mb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Name der Vorlage</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="z.B. Gastro Anschreiben" data-testid="template-name-input" className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">PDF-Datei</Label>
            <Input ref={fileRef} type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files[0])} data-testid="template-file-input" className="mt-1.5" />
          </div>
        </div>
        <Button type="submit" disabled={uploading} data-testid="template-upload-button" className="mt-5 bg-[#002FA7] hover:bg-[#001D6E] text-white rounded-sm">
          <UploadSimple size={16} className="mr-2" />
          {uploading ? "Lade hoch…" : "Vorlage hochladen"}
        </Button>
      </form>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="templates-list">
        {templates.length === 0 ? (
          <div className="col-span-full text-center text-zinc-500 py-12 bg-white border border-zinc-200 rounded-md">
            Noch keine Vorlagen vorhanden.
          </div>
        ) : (
          templates.map((t) => (
            <div key={t.id} className="bg-white border border-zinc-200 rounded-md p-5 hover:-translate-y-px hover:shadow-sm transition-all duration-150" data-testid={`template-card-${t.id}`}>
              <div className="flex items-start justify-between">
                <div className="h-9 w-9 bg-[#002FA7]/10 text-[#002FA7] grid place-items-center rounded-sm">
                  <FilePdf size={20} weight="bold" />
                </div>
                <button onClick={() => remove(t.id)} data-testid={`delete-template-${t.id}`} className="text-zinc-300 hover:text-[#E53935]">
                  <Trash size={16} />
                </button>
              </div>
              <h3 className="font-heading font-semibold mt-3 truncate" title={t.name}>{t.name}</h3>
              <p className="text-xs text-zinc-400 truncate">{t.filename}</p>
              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1.5 flex items-center gap-1">
                  <Code size={12} /> Erkannte Platzhalter ({t.variables.length})
                </div>
                <div className="flex flex-wrap gap-1">
                  {t.variables.length === 0 ? (
                    <span className="text-xs text-amber-600">Keine Platzhalter gefunden</span>
                  ) : (
                    t.variables.map((v) => (
                      <Badge key={v} variant="secondary" className="text-[10px] font-mono rounded-sm">{`{{${v}}}`}</Badge>
                    ))
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
